import { type ReactNode } from "react";
import { type LucideIcon } from "lucide-react";

export type StateTone =
  | "info"
  | "loading"
  | "success"
  | "warning"
  | "error"
  | "not-found";

type StateHeading = "h1" | "h2" | "h3";

interface StateCardProps {
  icon?: LucideIcon;
  tone?: StateTone;
  title: string;
  body: string;
  technicalDetail?: string;
  actions?: ReactNode;
  titleAs?: StateHeading;
  className?: string;
}

export function StateCard({
  icon: Icon,
  tone = "info",
  title,
  body,
  technicalDetail,
  actions,
  titleAs: Heading = "h1",
  className,
}: StateCardProps) {
  return (
    <section
      className={`w-full max-w-xl rounded-md border border-[rgb(var(--starling-border))] bg-[rgb(var(--starling-panel))] p-5 ${className ?? ""}`}
      aria-busy={tone === "loading" ? "true" : undefined}
    >
      <div className="flex items-start gap-3">
        {Icon ? (
          <div
            className={`grid size-9 shrink-0 place-items-center rounded-md border border-[rgb(var(--starling-border))] bg-[rgb(var(--starling-elevated))] text-[rgb(var(--starling-cyan))]${tone === "loading" ? " motion-safe:animate-spin" : ""}`}
            aria-hidden="true"
          >
            <Icon className="size-4" />
          </div>
        ) : null}
        <div className="min-w-0">
          <Heading className="text-base font-semibold text-[rgb(var(--starling-text-primary))]">
            {title}
          </Heading>
          <p className="mt-2 text-sm leading-6 text-[rgb(var(--starling-text-secondary))]">
            {body}
          </p>
        </div>
      </div>
      {technicalDetail ? (
        <p className="mt-4 rounded-md border border-[rgb(var(--starling-border))] bg-[rgb(var(--starling-bg)/0.42)] px-3 py-2 font-mono text-xs text-[rgb(var(--starling-text-tertiary))]">
          {technicalDetail}
        </p>
      ) : null}
      {actions ? (
        <div className="mt-5 flex flex-wrap gap-2">{actions}</div>
      ) : null}
    </section>
  );
}
